import { useFeatureFlags } from "../../hooks/useFeatureFlag";
import { useState, useEffect, useMemo } from "react";
import FeatureFlag, {
  FeatureFlagName,
} from "../../types/feature-flags/featureFlag";
import { useAllCustomers } from "../../hooks/useCustomer";
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import FeatureFlagResults, {
  FEATURE_FLAGS_DEFAULT_SORT_MODEL,
} from "./FeatureFlagResults";
import SelectCustomerFeatureFlags from "./components/SelectCustomerFeatureFlags";

export const FEATURE_FLAG_TABLE_ID = "manage-feature-flags";

export const ALL_FEATURE_FLAGS: FeatureFlagName[] = [
  FeatureFlagName.Transcription,
  FeatureFlagName.BulkExport,
  FeatureFlagName.Views,
];

export default function FeatureFlags() {
  const { data, isLoading } = useFeatureFlags();
  const { data: customers, isLoading: isCustomersLoading } = useAllCustomers();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    null
  );
  const [features, setFeatures] = useState<FeatureFlag[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [sortModel, setSortModel] = useState<GridSortModel>(
    FEATURE_FLAGS_DEFAULT_SORT_MODEL
  );
  const [paginationModel, setPaginationModel] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const handleSortModelChange = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  useEffect(() => {
    setFeatures(data?.featureFlags ?? []);
  }, [data]);

  const mergedFlags = useMemo(() => {
    if (!customers || isLoading) return [];

    const overrides = features ?? [];

    return customers.flatMap((customer) => {
      const { customerId, name: customerName } = customer;

      // Skip customers that don't match the selectedCustomerId
      if (selectedCustomerId && customerId !== selectedCustomerId) {
        return [];
      }

      return ALL_FEATURE_FLAGS.map((featureName) => {
        const override = overrides.find(
          (f) => f.customerId === customerId && f.featureName === featureName
        );

        return {
          customerId,
          customerName,
          featureName,
          isEnabled: override?.isEnabled ?? false,
          expiration: override?.expiration ?? null,
        };
      });
    });
  }, [customers, features, isLoading, selectedCustomerId]);

  useEffect(() => {
    if (data == null) return;
    setRowCount(mergedFlags.length);
  }, [data, mergedFlags]);

  const handlePaginationModelChange = ({
    page,
    pageSize,
  }: GridPaginationModel) => {
    setPaginationModel({ pageIndex: page, pageSize });
  };

  return (
    <div className="h-full flex flex-col p-4 w-full">
      <SelectCustomerFeatureFlags
        selectedCustomerId={selectedCustomerId}
        onChangeCustomer={setSelectedCustomerId}
      />
      <FeatureFlagResults
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        sortModel={sortModel}
        results={mergedFlags}
        tableId={FEATURE_FLAG_TABLE_ID}
        isLoading={isLoading || isCustomersLoading}
        refresh={() => {}}
        className="flex-1 flex-grow mt-2"
        onSortModelChange={handleSortModelChange}
        paginationModel={{
          page: paginationModel.pageIndex,
          pageSize: paginationModel.pageSize,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        rowCount={rowCount}
        setFeatures={setFeatures}
      />
    </div>
  );
}
